import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import CommentSection from '../CommentSection';
import VersionHistory from '../VersionHistory';
import { API } from './constants';
import { emitProgressRefresh } from '@/utils/progressEvents';

export function DeviationDetailModal({
  deviation,
  onClose,
  onUpdate,
  formatDate,
  formatDateTime,
  getSeverityColor,
  getSeverityLabel,
  getTypeConfig,
  RUH_CATEGORIES,
  t
}) {
  const [rootCause, setRootCause] = useState(deviation.root_cause || '');
  const [saving, setSaving] = useState(false);

  // Actions/Tiltak state
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [showAddAction, setShowAddAction] = useState(false);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionDescription, setNewActionDescription] = useState('');
  const [newActionResponsible, setNewActionResponsible] = useState('');
  const [newActionDeadline, setNewActionDeadline] = useState('');
  const [addingAction, setAddingAction] = useState(false);

  // Close confirmation state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const typeConfig = getTypeConfig(deviation.type);
  const isRUH = deviation.type === 'ruh';
  const isDeviation = deviation.type === 'deviation';
  const isClosed = deviation.status === 'closed';

  // Fetch actions on mount
  useEffect(() => {
    fetchActions();
  }, [deviation.id]);

  const fetchActions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/deviations/${deviation.id}/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActions(response.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    } finally {
      setLoadingActions(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(`${API}/ops/deviations/${deviation.id}`, {
        root_cause: rootCause || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('common.updated'));
      onUpdate();
    } catch (error) {
      toast.error(t('cases.couldNotSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    if (!newActionTitle.trim()) {
      toast.error('Tittel er påkrevd');
      return;
    }

    setAddingAction(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/deviations/${deviation.id}/actions`, {
        title: newActionTitle,
        description: newActionDescription || null,
        responsible: newActionResponsible || null,
        deadline: newActionDeadline ? new Date(newActionDeadline).toISOString() : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tiltak lagt til');
      setNewActionTitle('');
      setNewActionDescription('');
      setNewActionResponsible('');
      setNewActionDeadline('');
      setShowAddAction(false);
      fetchActions();
      onUpdate(); // Refresh parent list
    } catch (error) {
      toast.error(t('cases.errors.couldNotAddAction'));
    } finally {
      setAddingAction(false);
    }
  };

  const handleUpdateActionStatus = async (actionId, newStatus) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(`${API}/ops/actions/${actionId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tiltak oppdatert');
      fetchActions();
    } catch (error) {
      toast.error(t('cases.errors.couldNotUpdateAction'));
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette tiltaket?')) return;

    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tiltak slettet');
      fetchActions();
    } catch (error) {
      toast.error(t('cases.errors.couldNotDeleteAction'));
    }
  };

  const handleCloseSak = async (force = false) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/deviations/${deviation.id}/close`, null, {
        params: { force },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requires_confirmation) {
        setShowCloseConfirm(true);
        return;
      }

      toast.success('Sak lukket');
      emitProgressRefresh('deviation-closed');
      onUpdate();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('cases.couldNotClose');
      toast.error(errorMsg);
    }
  };

  const getActionStatusColor = (status) => {
    const colors = {
      'pending': 'bg-slate-700/50 text-slate-400',
      'in_progress': 'bg-amber-500/20 text-amber-400',
      'completed': 'bg-emerald-500/20 text-emerald-400'
    };
    return colors[status] || 'bg-slate-700/50 text-slate-400';
  };

  const getActionStatusLabel = (status) => {
    const labels = { 'pending': 'Venter', 'in_progress': 'Pågår', 'completed': 'Fullført' };
    return labels[status] || status;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">{typeConfig.label} detaljer</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors" data-testid="close-detail-modal">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Header info */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm px-2 py-1 rounded ${getSeverityColor(deviation.severity)}`}>
              {getSeverityLabel(deviation.severity)}
            </span>
            {isRUH && deviation.ruh_category && (
              <span className="text-sm px-2 py-1 rounded bg-slate-700/50 text-slate-300">
                {RUH_CATEGORIES[deviation.ruh_category]}
              </span>
            )}
            {deviation.escalated_to_deviation_id && (
              <span className="text-sm px-2 py-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                Eskalert til avvik
              </span>
            )}
            {isClosed && (
              <span className="text-sm px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Lukket
              </span>
            )}
          </div>

          {/* Title */}
          {deviation.title && (
            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1">Tittel</label>
              <p className="text-slate-200 font-medium">{deviation.title}</p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-500 mb-1">Beskrivelse</label>
            <p className="text-slate-200">{deviation.description}</p>
          </div>

          {/* RUH-specific info */}
          {isRUH && (
            <div className="grid grid-cols-2 gap-4 bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
              {deviation.incident_datetime && (
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Når</label>
                  <p className="text-slate-200">{formatDateTime(deviation.incident_datetime)}</p>
                </div>
              )}
              {deviation.location && (
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Hvor</label>
                  <p className="text-slate-200">{deviation.location}</p>
                </div>
              )}
              {deviation.immediate_actions && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-500 mb-1">Umiddelbare tiltak</label>
                  <p className="text-slate-200">{deviation.immediate_actions}</p>
                </div>
              )}
            </div>
          )}

          {/* Root cause analysis */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Årsaksanalyse</label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              rows={3}
              placeholder="Hva var rotårsaken til hendelsen/avviket?"
              disabled={isClosed}
              data-testid="root-cause-input"
            />
          </div>

          {/* ========== TILTAK (ACTIONS) SECTION ========== */}
          <div className="border-t border-slate-800 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">
                Tiltak ({actions.length})
                {isDeviation && actions.length === 0 && !isClosed && (
                  <span className="ml-2 text-sm font-normal text-rose-400">* Påkrevd for å lukke</span>
                )}
                {isRUH && actions.length === 0 && !isClosed && (
                  <span className="ml-2 text-sm font-normal text-orange-400">* Anbefalt</span>
                )}
              </h3>
              {!isClosed && (
                <button
                  onClick={() => setShowAddAction(!showAddAction)}
                  className="px-3 py-1.5 bg-cyan-500 text-slate-950 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors"
                  data-testid="add-action-btn"
                >
                  + Legg til tiltak
                </button>
              )}
            </div>

            {/* Add action form */}
            {showAddAction && (
              <form onSubmit={handleAddAction} className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 mb-4 space-y-3" data-testid="add-action-form">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Tiltak *</label>
                  <input
                    type="text"
                    value={newActionTitle}
                    onChange={(e) => setNewActionTitle(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    placeholder="Beskriv tiltaket..."
                    autoFocus
                    data-testid="action-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Beskrivelse</label>
                  <textarea
                    value={newActionDescription}
                    onChange={(e) => setNewActionDescription(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    rows={2}
                    placeholder="Ytterligere detaljer..."
                    data-testid="action-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Ansvarlig</label>
                    <input
                      type="text"
                      value={newActionResponsible}
                      onChange={(e) => setNewActionResponsible(e.target.value)}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                      placeholder="Navn"
                      data-testid="action-responsible-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Frist</label>
                    <input
                      type="date"
                      value={newActionDeadline}
                      onChange={(e) => setNewActionDeadline(e.target.value)}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                      data-testid="action-deadline-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowAddAction(false)}
                    className="px-3 py-1.5 border border-slate-600 text-slate-400 rounded-lg text-sm hover:bg-slate-800 transition-colors"
                    data-testid="cancel-add-action"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={addingAction}
                    className="px-3 py-1.5 bg-cyan-500 text-slate-950 rounded-lg text-sm font-medium hover:bg-cyan-400 disabled:opacity-50 transition-colors"
                    data-testid="submit-add-action"
                  >
                    {addingAction ? 'Legger til...' : 'Legg til'}
                  </button>
                </div>
              </form>
            )}

            {/* Actions list */}
            {loadingActions ? (
              <div className="text-center py-4 text-slate-500">{t('cases.loadingActions')}</div>
            ) : actions.length === 0 ? (
              <div className="text-center py-4 text-slate-500 bg-slate-800/30 rounded-xl" data-testid="no-actions-message">
                {t('cases.actions.noActions') || 'Ingen tiltak registrert ennå'}
              </div>
            ) : (
              <div className="space-y-2" data-testid="actions-list">
                {actions.map(action => (
                  <div key={action.id} className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50" data-testid={`action-item-${action.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${getActionStatusColor(action.status)}`}>
                            {getActionStatusLabel(action.status)}
                          </span>
                          {action.deadline && (
                            <span className="text-xs text-slate-500">
                              Frist: {formatDate(action.deadline)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-200 font-medium">{action.title}</p>
                        {action.description && (
                          <p className="text-sm text-slate-400 mt-1">{action.description}</p>
                        )}
                        {action.responsible && (
                          <p className="text-xs text-slate-500 mt-1">Ansvarlig: {action.responsible}</p>
                        )}
                      </div>
                      {!isClosed && (
                        <div className="flex items-center gap-1 ml-2">
                          {action.status !== 'completed' && (
                            <button
                              onClick={() => handleUpdateActionStatus(action.id, 'completed')}
                              className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors"
                              title="Marker som fullført"
                              data-testid={`complete-action-${action.id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          )}
                          {action.status === 'pending' && (
                            <button
                              onClick={() => handleUpdateActionStatus(action.id, 'in_progress')}
                              className="p-1.5 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
                              title="Marker som pågår"
                              data-testid={`start-action-${action.id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteAction(action.id)}
                            className="p-1.5 text-rose-400 hover:bg-rose-500/20 rounded-lg transition-colors"
                            title="Slett"
                            data-testid={`delete-action-${action.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-800">
            <div>
              <label className="block text-xs text-slate-500">Opprettet</label>
              <p className="text-sm text-slate-300">{formatDate(deviation.created_at)}</p>
            </div>
            {deviation.responsible && (
              <div>
                <label className="block text-xs text-slate-500">Ansvarlig</label>
                <p className="text-sm text-slate-300">{deviation.responsible}</p>
              </div>
            )}
            {deviation.deadline && (
              <div>
                <label className="block text-xs text-slate-500">Frist</label>
                <p className="text-sm text-slate-300">{formatDate(deviation.deadline)}</p>
              </div>
            )}
            {deviation.closed_at && (
              <div>
                <label className="block text-xs text-slate-500">Lukket</label>
                <p className="text-sm text-slate-300">{formatDate(deviation.closed_at)}</p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex justify-between pt-4 border-t border-slate-800">
            {!isClosed && (
              <button
                onClick={() => handleCloseSak(false)}
                className="px-4 py-2 bg-emerald-500 text-slate-950 rounded-xl font-medium hover:bg-emerald-400 transition-colors"
                data-testid="close-sak-btn"
              >
                {t('cases.closeCase')}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || isClosed}
              className={`px-4 py-2 bg-cyan-500 text-slate-950 rounded-xl font-medium hover:bg-cyan-400 disabled:opacity-50 transition-colors ${isClosed ? 'hidden' : ''}`}
              data-testid="save-changes-btn"
            >
              {saving ? t('cases.saving') : t('cases.saveChanges')}
            </button>
          </div>
        </div>

        {/* Comments */}
        <CommentSection entityType="case" entityId={deviation.id} />

        {/* Version History */}
        <VersionHistory entityType="case" entityId={deviation.id} onRestored={onUpdate} />

        {/* Close confirmation modal for RUH */}
        {showCloseConfirm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]" data-testid="close-confirm-modal">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-white mb-2">{t('cases.closeConfirm.title')}</h3>
              <p className="text-slate-400 mb-4">
                {t('cases.closeConfirm.ruhWarning')}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCloseConfirm(false)}
                  className="px-4 py-2 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-800 transition-colors"
                  data-testid="cancel-close-confirm"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowCloseConfirm(false);
                    handleCloseSak(true);
                  }}
                  className="px-4 py-2 bg-orange-500 text-slate-950 rounded-xl font-medium hover:bg-orange-400 transition-colors"
                  data-testid="confirm-close-without-actions"
                >
                  {t('cases.closeConfirm.closeWithoutActions')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
